interface SignupsChartProps {
  data: { date: string; count: number }[];
  days?: number;
}

// Build a dense last-N-day series (UTC, matching the server's date()) from the
// sparse rows, filling missing days with 0.
function densify(data: { date: string; count: number }[], days: number) {
  const counts = new Map(data.map((d) => [d.date, d.count]));
  const out: { date: string; count: number }[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, count: counts.get(key) ?? 0 });
  }
  return out;
}

function fmt(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function SignupsChart({ data, days = 30 }: SignupsChartProps) {
  const series = densify(data, days);
  const peak = Math.max(1, ...series.map((d) => d.count));
  const total = series.reduce((s, d) => s + d.count, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <span className="text-2xl font-semibold text-slate-900 dark:text-slate-100 font-mono">{total}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">in the last {days} days</span>
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500">peak {peak}/day</span>
      </div>

      {/* Bars — one column per day; empty days are hoverable too. */}
      <div className="flex items-end gap-[2px] h-36 border-b border-slate-200 dark:border-slate-700">
        {series.map((d) => {
          const h = (d.count / peak) * 100;
          return (
            <div key={d.date} className="group relative flex-1 flex items-end h-full">
              {d.count > 0 && (
                <div
                  className="w-full rounded-t bg-primary/80 group-hover:bg-primary transition-colors min-h-[2px]"
                  style={{ height: `${h}%` }}
                />
              )}
              {/* Tooltip */}
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 whitespace-nowrap rounded bg-slate-900 dark:bg-slate-700 text-white text-xs px-2 py-1 shadow">
                {fmt(d.date)}: {d.count} signup{d.count === 1 ? '' : 's'}
              </div>
            </div>
          );
        })}
      </div>

      {/* X ticks: oldest → today */}
      <div className="flex justify-between text-xs text-slate-400 dark:text-slate-500 mt-2">
        <span>{fmt(series[0].date)}</span>
        <span>{fmt(series[series.length - 1].date)}</span>
      </div>
    </div>
  );
}
